import { Checkbox, ExpandToggle, Typography } from '@weareopenr/component-library';
import styles from './RoleModal.module.scss';
import {
  allGroupActions,
  IPermissionAction,
  IPermissionGroup,
  isDestructiveAction,
  isGroupFullySelected,
  isGroupPartiallySelected,
} from './permissions';

interface IPermissionGroupCardProps {
  group: IPermissionGroup;
  visibleGroup?: IPermissionGroup;
  selectedPermissions: string[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onToggleGroup: (checked: boolean) => void;
  onToggleAction: (slug: string, checked: boolean) => void;
}

interface IPermissionRowProps {
  action: IPermissionAction;
  isChecked: boolean;
  onToggle: (slug: string, checked: boolean) => void;
}

const PermissionRow = ({ action, isChecked, onToggle }: IPermissionRowProps) => {
  if (action.disabled) {
    return (
      <div className={styles['permission-row']}>
        <Typography size="small" weight="semi-bold" className={styles['row-label-muted']}>
          {action.label}
        </Typography>
        <span className={styles['included-badge']}>Included for every role</span>
      </div>
    );
  }

  const { description } = action;
  const destructive = isDestructiveAction(action.action);

  return (
    <div className={styles['permission-row']}>
      <Checkbox
        label={action.label}
        checked={isChecked}
        onChange={(event) => onToggle(action.slug, event.target.checked)}
      />
      {description && (
        <Typography
          size="small"
          className={
            destructive ? styles['row-description-destructive'] : styles['row-description']
          }
        >
          {description}
        </Typography>
      )}
    </div>
  );
};

const PermissionGroupCard = ({
  group,
  visibleGroup,
  selectedPermissions,
  isExpanded,
  onToggleExpand,
  onToggleGroup,
  onToggleAction,
}: IPermissionGroupCardProps) => {
  const rows = visibleGroup ?? group;
  const selectableActions = allGroupActions(group);
  const selectedCount = selectableActions.filter((action) =>
    selectedPermissions.includes(action.slug),
  ).length;

  return (
    <section aria-label={`${group.label} permissions`} className={styles['group-card']}>
      <div className={styles['group-header']}>
        <span className={styles['group-checkbox']}>
          <Checkbox
            label={group.label}
            checked={isGroupFullySelected(selectedPermissions, group)}
            indeterminate={isGroupPartiallySelected(selectedPermissions, group)}
            onChange={(event) => onToggleGroup(event.target.checked)}
          />
        </span>
        <Typography size="small" className={styles['group-description']}>
          {group.description}
        </Typography>
        <span
          className={styles['group-count']}
        >{`${selectedCount} of ${selectableActions.length}`}</span>
        <ExpandToggle
          isExpanded={isExpanded}
          className={styles['group-toggle']}
          onClick={onToggleExpand}
        />
      </div>
      {isExpanded && (
        <fieldset className={styles['permission-rows']} aria-label={group.label}>
          {rows.actions.map((action) => (
            <PermissionRow
              key={action.slug}
              action={action}
              isChecked={selectedPermissions.includes(action.slug)}
              onToggle={onToggleAction}
            />
          ))}
          {rows.children.map((child) => (
            <fieldset
              key={child.collection}
              aria-label={child.label}
              className={styles['subgroup']}
            >
              <div className={styles['subgroup-header']}>
                <Typography size="small" weight="bold">
                  {child.label}
                </Typography>
                <Typography size="small" className={styles['subgroup-description']}>
                  {child.description}
                </Typography>
              </div>
              {child.actions.map((action) => (
                <PermissionRow
                  key={action.slug}
                  action={action}
                  isChecked={selectedPermissions.includes(action.slug)}
                  onToggle={onToggleAction}
                />
              ))}
            </fieldset>
          ))}
        </fieldset>
      )}
    </section>
  );
};

export default PermissionGroupCard;
